import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { api } from './api';
import { PerfOverlay } from './components/PerfOverlay';
import { ShareSourcePicker } from './components/ShareSourcePicker';
import { CallSurfaces } from './components/CallSurfaces';
import { Toasts } from './components/Toasts';
import { engine } from './engine';
import { AccountPage } from './pages/AccountPage';
import { AdminPage } from './pages/AdminPage';
import { AppShell } from './components/AppShell';
import { CalendarPage } from './pages/CalendarPage';
import { CallPage } from './pages/CallPage';
import { HistoryPage } from './pages/HistoryPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { MeetingPage } from './pages/MeetingPage';
import { ChatPage } from './pages/ChatPage';
import { PeopleDirectoryPage } from './pages/PeopleDirectoryPage';
import { PeoplePage } from './pages/PeoplePage';
import { SecurityPage } from './pages/SecurityPage';
import { ensureReady as ensureChatReady } from './chat/chatClient';
import { startPresence, stopPresence } from './presence';
import { unlockAudio } from './sounds';
import { bindEngineToStore } from './store/callStore';

// Engine → store binding happens ONCE at module scope, outside the React tree
// (SPEC §9.3.1). React below only renders store snapshots.
bindEngineToStore(engine);

// Arm the audio unlock immediately: an incoming call arrives over a socket with
// no user gesture, so the context must already be running by then or the
// ringtone is silently dropped.
unlockAudio();

/** Holds the presence socket open for the whole signed-in session (Layer B1):
 * it marks you online and streams other people's status transitions. Renders
 * nothing — events land in presenceStore, outside the React tree (§9.3.1). */
function PresenceGate() {
  useEffect(() => {
    let cancelled = false;
    // Only signed-in users have presence; a guest in a meeting lobby does not.
    api
      .me()
      .then((me) => {
        if (cancelled) return;
        startPresence(me.id);
        // Enroll this device for E2EE chat (Layer CH) so the person is
        // addressable before they ever open the Chat tab. Fire-and-forget:
        // chat failing must never break presence or calls.
        void ensureChatReady(me.id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stopPresence();
    };
  }, []);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Meetings + Calls share one shell, so the top bar never remounts. */}
        <Route element={<AppShell />}>
          <Route
            path="/"
            element={
              window.location.hostname.startsWith('admin.') ? (
                <Navigate to="/admin" replace />
              ) : (
                <HomePage />
              )
            }
          />
          <Route path="/people" element={<PeopleDirectoryPage />} />
          <Route path="/calls" element={<PeoplePage />} />
          <Route path="/chat" element={<ChatPage />} />
          {/* Catch-all, inside the shell so an unknown URL still has chrome and
              a way back. Route ranking puts it last however it is written. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/call/:id" element={<CallPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/m/:slug" element={<MeetingPage />} />
      </Routes>
      <Toasts />
      <PresenceGate />
      {/* Incoming/outgoing call surfaces live above the router (Layer B3). */}
      <CallSurfaces />
      {/* Electron-only custom screen-share picker (F-CALL-2); no-op in browser. */}
      <ShareSourcePicker />
      <PerfOverlay />
    </BrowserRouter>
  );
}
